<template>
  <div class="fill-height">
    <div class="drop-box">
      <file-pond ref="pond" name="file" credits="false" :allow-multiple="true" :max-files="maxFiles"
        :accepted-file-types="acceptedFileTypes" :max-file-size="maxFileSize" :files="files" :server="server"
        :label-idle="labelIdle" :allow-file-type-validation="true"
        :allow-file-size-validation="true" :file-validate-type-detect-type="detectFileType" :allow-revert="false"
        :allow-process="true" :allow-remove="true" :remove-after-process="false" :allow-reprocess="false"
        :file-metadata="{ fileIcon: getFileIcon }" :file-rename="getFileRename" :revert-after-upload="false"
        :instant-upload="true" :keep-files-after-processing="true"
        class="file-pond-container" @addfile="handleFileAdded" @removefile="handleFileRemoved"
        @processfile="handleFileProcessed" @processfiles="handleProcessFiles" @error="handleError" />
    </div>

    <div class="d-flex justify-end mt-2" v-if="hasUnprocessedFiles">
      <v-btn color="primary" @click="handleProcessFiles" :loading="isUploading" :disabled="isUploading">
        Upload Files
      </v-btn>
    </div>
  </div>
</template>
<script>
import * as api from "../../utils/api.js";
import * as fhirHelper from "../../utils/fhir-helpers";
import { mapState, mapActions } from "vuex";
import { CREATE_DOCUMENT, CREATE_DOCUMENT_FAILURE, CREATE_DOCUMENT_COMPLETE } from "../../store/patients/mutation-types";
import { SNACKBAR_ERROR, SLOW_CONNECTION, SLOW_CONNECTION_RESOLVED } from "../../store/appMutations";

// Import Vue FilePond
import vueFilePond from 'vue-filepond';

// Import FilePond plugins
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileRename from 'filepond-plugin-file-rename';
import FilePondPluginFileMetadata from 'filepond-plugin-file-metadata';

// Import FilePond styles
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';

// FilePond component with plugins

// Create FilePond component
const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginFileRename,
  FilePondPluginFileMetadata
);

export default {
  name: "PatientFileUpload",
  components: {
    FilePond
  },
  beforeRouteLeave(_to, _from, next) {
    if (this.filesInProgress.length) {
      if (!confirm("Uploads are in progress. Leave anyway?")) {
        return;
      }
    }
    next();
  },
  beforeUnmount() {
    // Clean up any timeouts or resources
    if (this.isUploading) {
      console.log('Component unmounting while uploads in progress - cleaning up');
      this.isUploading = false;
    }

    // Clear any files that might be in progress
    if (this.$refs.pond) {
      const pondFiles = this.$refs.pond.getFiles();
      if (pondFiles && pondFiles.length > 0) {
        console.log(`Cleaning up ${pondFiles.length} files on unmount`);
      }
    }
  },
  props: {
    patientID: {
      type: String,
      required: true
    },
    maxFiles: {
      type: Number,
      default: null
    },
    acceptedFileTypes: {
      type: Array,
      default: () => ['image/*', '.pdf', 'application/pdf', '.txt', '.csv', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip']
    },
    maxFileSize: {
      type: String,
      default: '10000MB'
    }
  },
  data: () => ({
    filesToUpload: [],
    files: [],
    currentFile: null,
    isUploading: false,
    uploadQueue: [],
    fileErrors: {},
    fileIcons: {
      pdf: 'mdi-file-pdf-box',
      doc: 'mdi-file-word-box',
      docx: 'mdi-file-word-box',
      xls: 'mdi-file-excel-box',
      xlsx: 'mdi-file-excel-box',
      ppt: 'mdi-file-powerpoint-box',
      pptx: 'mdi-file-powerpoint-box',
      txt: 'mdi-file-document-box',
      csv: 'mdi-file-delimited-box',
      zip: 'mdi-folder-zip-box',
      image: 'mdi-file-image-box',
      default: 'mdi-file-box'
    },
    labelIdle: 'Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
  }),
  computed: {
    ...mapState("register", ["error", "instanceID"]),
    ...mapState("instances", ["instance", "loading"]),
    filesInProgress() {
      return this.filesToUpload.filter(f => f.inProgress || f.processing);
    },
    currentInstanceID() {
      return this.instance?.id || this.instanceID;
    },
    hasUnprocessedFiles() {
      if (!this.$refs.pond) return false;
      const pondFiles = this.$refs.pond.getFiles();
      return pondFiles && pondFiles.length > 0 && pondFiles.some(file => !file.processing && !file.processed);
    },
    server() {
      return {
        // Add a revert property that does nothing to prevent DELETE requests
        revert: (uniqueFileId, load) => {
          console.log('Revert called for file - INTERCEPTED AND BLOCKED:', uniqueFileId);

          // Find the file in FilePond
          if (this.$refs.pond) {
            const pondFiles = this.$refs.pond.getFiles();
            const file = pondFiles.find(f => f.serverId === uniqueFileId || f.id === uniqueFileId);
            if (file) {
              console.log('Found file that FilePond is trying to revert:', file.filename);
              // Mark as processed to prevent further revert attempts
              file.processed = true;
              file.status = 5; // Status 5 is "PROCESSING_COMPLETE"
            }
          }

          // Just call load to indicate success without actually doing anything
          load();
          return;
        },
        process: (_fieldName, file, metadata, load, error, progress, abort) => {
          // Ensure file has an ID - use metadata, existing ID, or generate a new one
          if (!file.id && metadata.fileId) {
            file.id = metadata.fileId;
          } else if (!file.id) {
            file.id = `file_${new Date().getTime()}_${file.name}`;
          }

          console.log("🚀 ~ server ~ processing file:", file.id, file.name);

          // Create FormData
          const formData = new FormData();
          formData.append('file', file);

          // Store file metadata for later use
          const fileKey = new Date().toISOString() + file.name;
          file.description = metadata.description || file.name;

          // Create file object and add to list
          let fileToUpload = {
            data: file,
            inProgress: true,
            progress: 0,
            processing: true,
            error: false,
            key: fileKey,
            // Store file ID - ensure it's not undefined
            id: file.id || fileKey, // Use fileKey as fallback if file.id is undefined
            prevLoaded: 0,
            slowConnection: false
          };

          // Assign ID to file object if it doesn't have one
          if (!file.id) {
            file.id = fileKey;
          }

          // Check if this file is already being processed
          const existingFileIndex = this.filesToUpload.findIndex(f =>
            (f.id === file.id) || (f.data && f.data.name === file.name)
          );

          if (existingFileIndex >= 0) {
            console.warn(`File ${file.id} is already being processed, replacing entry`);
            this.filesToUpload.splice(existingFileIndex, 1);
          }

          this.filesToUpload.push(fileToUpload);
          console.log(`Added file to upload queue. Current queue size: ${this.filesToUpload.length}`);

          // Upload to Binary endpoint
          this.CREATE_DOCUMENT();
          const patientHeader = { patientID: this.patientID };
          const parent = this;

          api.createBinary(
            formData,
            {
              headers: { ...patientHeader },
              onUploadProgress: function (progressEvent) {
                fileToUpload.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                progress(progressEvent.loaded, progressEvent.total);

                const rate = progressEvent.loaded - fileToUpload.prevLoaded;
                const minRate = 100000;
                if (!fileToUpload.slowConnection && fileToUpload.prevLoaded > minRate && rate < minRate) {
                  fileToUpload.slowConnection = true;
                  parent.SLOW_CONNECTION();
                } else if (rate >= minRate) {
                  fileToUpload.slowConnection = false;
                  if (!parent.filesToUpload.filter(f => f.slowConnection)) parent.SLOW_CONNECTION_RESOLVED();
                }
                fileToUpload.prevLoaded = progressEvent.loaded;
              },
            },
            { timeout: 600000 }
          )
            .then(binary => {
              fileToUpload.progress = 100; // Ensure progress shows 100% before processing starts
              file.binaryID = binary.data.id;

              // Create DocumentReference
              const fileData = {
                name: file.name,
                type: file.type,
                size: file.size,
                binaryID: binary.data.id,
                description: file.description
              };

              return api.post("DocumentReference",
                fhirHelper.createDocumentReference(fileData, this.patientID, this.currentInstanceID)
              );
            })
            .then(doc => {
              fileToUpload.inProgress = false;
              fileToUpload.processing = false;

              // Mark file as processed in multiple ways to ensure it sticks
              file.processed = true;
              file.status = 5; // Status 5 is "PROCESSING_COMPLETE"

              // Prevent FilePond from trying to revert the file
              file.revert = null;

              // Store the document ID directly on the file
              file.serverId = doc.id;

              // Update filesToUpload entry
              parent.filesToUpload = parent.filesToUpload.filter(f => f.key !== fileKey);

              if (!parent.filesToUpload.filter(f => f.slowConnection).length) parent.SLOW_CONNECTION_RESOLVED();

              parent.CREATE_DOCUMENT_COMPLETE(doc);

              // Try to set document ID in metadata
              try {
                if (typeof file.setMetadata === 'function') {
                  file.setMetadata('documentId', doc.id);
                  file.setMetadata('processed', 'true');
                  file.setMetadata('status', '5'); // Status 5 is "PROCESSING_COMPLETE"
                }
              } catch (err) {
                console.warn('Could not set metadata on file:', err);
              }

              // Call load with the document ID to signal completion to FilePond
              // This tells FilePond the file is processed successfully
              load(doc.id);

              // Emit the process event
              parent.$emit('onProcess', doc);

              // Log success
              console.log(`File ${file.id} (${file.name}) processed successfully with document ID: ${doc.id}`);
            })
            .catch(err => {
              console.error(`Error uploading file ${file.id}:`, err);
              fileToUpload.error = true;

              let msg = "A file failed to upload. Please check your connection and try again.";
              if (err.response) {
                if (err.response.data && typeof err.response.data === 'string' &&
                  err.response.data.includes('File type not allowed')) {
                  msg = "This file type is not allowed. Please upload only supported file types (images, PDF, documents, etc.)";
                } else {
                  msg = err.response.data || msg;
                }
              }

              // Store error in fileErrors
              parent.fileErrors[file.id] = msg;

              // Log detailed error information
              console.error(`File upload error for ${file.name} (${file.id}):`, {
                error: err,
                fileToUpload,
                errorMessage: msg
              });

              parent.SNACKBAR_ERROR(msg);
              parent.CREATE_DOCUMENT_FAILURE();
              error(msg);
              parent.$emit('onError', { error: err, file, message: msg });
            });

          return {
            abort: () => {
              console.log(`Abort requested for file: ${file.id}`);

              // Don't abort if file is in progress or completed
              if (fileToUpload.inProgress || fileToUpload.progress > 0) {
                console.log(`File ${file.id} is in progress, preventing abort`);
                return false;
              }

              console.log(`Proceeding with abort for file: ${file.id}`);
              abort();

              // Clean up tracking arrays
              this.filesToUpload = this.filesToUpload.filter(f =>
                f.key !== fileKey && (f.id !== file.id || f.data?.name !== file.name)
              );
              this.uploadQueue = this.uploadQueue.filter(id => id !== file.id);

              if (this.fileErrors[file.id]) {
                delete this.fileErrors[file.id];
              }

              return true;
            }
          };
        }
      };
    }
  },
  methods: {
    ...mapActions("patients", [CREATE_DOCUMENT, CREATE_DOCUMENT_FAILURE, CREATE_DOCUMENT_COMPLETE]),
    ...mapActions([SNACKBAR_ERROR, SLOW_CONNECTION, SLOW_CONNECTION_RESOLVED]),
    handleFileAdded(error, file) {
      if (error) {
        this.SNACKBAR_ERROR(error.message);
        this.fileErrors[file.id || file.filename] = error.message;
        return;
      }

      // Generate a unique ID if file doesn't have one
      if (!file.id) {
        file.id = `file_${new Date().getTime()}_${file.filename}`;
      }

      this.currentFile = file;
      console.log('File added:', file.id, file.filename);

      // Add to upload queue if not already there
      if (!this.uploadQueue.includes(file.id)) {
        this.uploadQueue.push(file.id);
      }

      // Automatically set description to filename
      file.setMetadata('description', file.filename);

      // Store file ID in metadata for later retrieval
      file.setMetadata('fileId', file.id);

      // Set file icon based on file type
      const fileExtension = file.filename ? file.filename.split('.').pop().toLowerCase() : '';
      let iconName = this.fileIcons.default;

      if (file.fileType && file.fileType.startsWith('image/')) {
        iconName = this.fileIcons.image;
      } else if (fileExtension && this.fileIcons[fileExtension]) {
        iconName = this.fileIcons[fileExtension];
      }

      // Set icon in metadata - use the Unicode character code
      file.setMetadata('fileIcon', this.getIconCode(iconName));
    },

    // Process all files at once
    handleProcessFiles() {
      console.log('Processing all files...');
      const pondFiles = this.$refs.pond.getFiles();

      if (pondFiles && pondFiles.length > 0) {
        this.isUploading = true;

        // Log all files in the pond with detailed status
        console.log(`Found ${pondFiles.length} files in FilePond:`,
          pondFiles.map(f => ({
            id: f.id,
            filename: f.filename,
            status: f.status,
            processed: f.processed || false,
            processing: f.processing || false,
            completed: f.completed || false,
            successfullyProcessed: f.successfullyProcessed || false,
            hasError: f.hasError || false,
            hasFileError: this.fileErrors[f.id] ? true : false
          })));

        // Filter out files that are already processed, processing, or have errors
        const filesToProcess = pondFiles.filter(file =>
          !file.processing &&
          !file.processed &&
          !file.completed &&
          !file.successfullyProcessed &&
          !this.fileErrors[file.id] &&
          !file.hasError
        );

        console.log(`Found ${filesToProcess.length} files that need processing`);

        if (filesToProcess.length === 0) {
          console.log('No files need processing, setting upload state to complete');
          this.isUploading = false;
          return;
        }

        // Process files one by one with a small delay between each to avoid race conditions
        const processFilesSequentially = async () => {
          for (const file of filesToProcess) {
            // Double-check the file still needs processing (might have changed since we started)
            if (!file.processing &&
                !file.processed &&
                !file.completed &&
                !file.successfullyProcessed &&
                !this.fileErrors[file.id] &&
                !file.hasError) {

              console.log(`Processing file: ${file.id} - ${file.filename}`);

              try {
                // Mark the file as being processed to prevent duplicate processing
                file.isBeingProcessed = true;

                // Process the file
                await this.$refs.pond.processFile(file.id);

                // Mark as processed to prevent reprocessing
                file.processed = true;
                file.completed = true;
                file.successfullyProcessed = true;

                // Small delay between files to avoid overwhelming the server
                await new Promise(resolve => setTimeout(resolve, 500));
              } catch (err) {
                console.error(`Error processing file ${file.id}:`, err);
                this.fileErrors[file.id] = err.message || 'Unknown error during processing';
                file.hasError = true;
                this.SNACKBAR_ERROR(`Error processing file ${file.filename}: ${err.message || 'Unknown error'}`);
              } finally {
                // Clear the processing flag
                file.isBeingProcessed = false;
              }
            } else {
              console.log(`Skipping file ${file.id} - ${file.filename} as it's already processed or has errors`);
            }
          }

          // Check if all files are now processed
          const remainingFiles = this.$refs.pond.getFiles().filter(file => {
            return !file.processed &&
                   !file.completed &&
                   !file.successfullyProcessed &&
                   !this.fileErrors[file.id] &&
                   !file.hasError &&
                   !file.processing &&
                   !file.isBeingProcessed;
          });

          if (remainingFiles.length === 0) {
            console.log('All files have been processed or have errors');
            this.isUploading = false;
          } else {
            console.log(`${remainingFiles.length} files still need processing`);
          }
        };

        // Start sequential processing
        processFilesSequentially().catch(err => {
          console.error('Error in sequential file processing:', err);
          this.isUploading = false;
          this.SNACKBAR_ERROR('Error processing files. Please try again.');
        });

        // Safety timeout still needed but extended
        setTimeout(() => {
          if (this.isUploading) {
            console.warn('Upload timeout reached - forcing upload state to complete');
            this.isUploading = false;
          }
        }, 120000); // Extended to 2 minutes for multiple files
      }
    },
    handleFileRemoved(error, file) {
      if (error) {
        this.SNACKBAR_ERROR(error.message);
        return;
      }

      console.log('File removed:', file.id, file.filename);

      // Remove from upload queue
      this.uploadQueue = this.uploadQueue.filter(id => id !== file.id);

      // Remove from errors if it was there
      if (this.fileErrors[file.id]) {
        delete this.fileErrors[file.id];
      }

      // Remove from filesToUpload if it's there
      this.filesToUpload = this.filesToUpload.filter(f => f.id !== file.id);

      this.$emit('onRemove', file);
    },
    handleFileProcessed(error, file) {
      if (error) {
        this.SNACKBAR_ERROR(error.message);
        this.fileErrors[file.id] = error.message;
        console.error('Error processing file:', file.id, error);
        return;
      }

      console.log('File processed successfully', file.id, file.filename);

      // Mark file as processed
      file.processed = true;

      // Prevent FilePond from trying to revert the file
      file.revert = null;

      // Try to set metadata if the method exists
      try {
        if (typeof file.setMetadata === 'function') {
          file.setMetadata('processed', 'true');
        }
      } catch (err) {
        console.warn('Could not set metadata on file:', err);
      }

      // Remove from upload queue
      this.uploadQueue = this.uploadQueue.filter(id => id !== file.id);

      // Remove from errors if it was there
      if (this.fileErrors[file.id]) {
        delete this.fileErrors[file.id];
      }

      // Check if all files are processed
      if (this.$refs.pond) {
        const pondFiles = this.$refs.pond.getFiles();
        console.log('Files remaining:', pondFiles.length);

        // Log file statuses
        console.log('File statuses:', pondFiles.map(f => ({
          id: f.id,
          filename: f.filename,
          processed: f.processed,
          processing: f.processing,
          status: f.status
        })));

        // Check if all files are processed
        const allProcessed = !pondFiles.some(f => !f.processed && !f.processing);
        if (allProcessed) {
          console.log('All files processed');
          this.isUploading = false;
        }
      }

      // Emit event
      this.$emit('onFileProcessed', { file });
    },
    handleError(error, file, status) {
      console.error('FilePond error:', error, status);

      // Enhanced error logging for file type validation issues
      if (file && error.main === 'File is of invalid type') {
        console.log('File details:', {
          name: file.filename,
          type: file.fileType,
          size: file.fileSize,
          extension: file.filename ? file.filename.split('.').pop() : 'unknown'
        });

        // Check if it's a PDF with incorrect MIME type
        const fileExtension = file.filename ? file.filename.split('.').pop().toLowerCase() : '';
        if (fileExtension === 'pdf' && file.fileType !== 'application/pdf') {
          console.warn('PDF file detected with incorrect MIME type:', file.fileType);
          // You could potentially override the validation here if needed
        }
      }

      this.SNACKBAR_ERROR(error.message || "An error occurred during file upload");
      this.$emit('onError', { error, file, status });
    },

    // Get file icon based on file type
    getFileIcon(file) {
      if (!file || !file.fileType) return this.getIconCode(this.fileIcons.default);

      // Check if it's an image
      if (file.fileType.startsWith('image/')) {
        return this.getIconCode(this.fileIcons.image);
      }

      // Get extension from filename
      const extension = file.filename ? file.filename.split('.').pop().toLowerCase() : '';

      // Return icon based on extension
      const iconName = this.fileIcons[extension] || this.fileIcons.default;
      return this.getIconCode(iconName);
    },

    // Convert icon name to Unicode character code
    getIconCode(iconName) {
      // Material Design Icons mapping
      const iconMap = {
        'mdi-file-pdf-box': '\uF0225',
        'mdi-file-word-box': '\uF022C',
        'mdi-file-excel-box': '\uF021B',
        'mdi-file-powerpoint-box': '\uF0227',
        'mdi-file-document-box': '\uF0219',
        'mdi-file-delimited-box': '\uF0218',
        'mdi-folder-zip-box': '\uF0755',
        'mdi-file-image-box': '\uF021F',
        'mdi-file-box': '\uF0214'
      };

      return iconMap[iconName] || iconMap['mdi-file-box'];
    },

    // Get file rename options
    getFileRename() {
      return {
        // This ensures the filename is displayed properly
        sanitizeFileName: (name) => name
      };
    },

    // Custom file type detection to handle PDF files with incorrect MIME types
    detectFileType(source, type) {
      return new Promise((resolve) => {
        // If we already have a type and it's valid, use it
        if (type && this.acceptedFileTypes.includes(type)) {
          return resolve(type);
        }

        // Check file extension for PDFs
        if (source instanceof File) {
          const fileName = source.name.toLowerCase();
          if (fileName.endsWith('.pdf')) {
            console.log('Detected PDF file by extension:', fileName);
            return resolve('application/pdf');
          }

          // For other file types, extract extension and map to MIME type
          const extension = fileName.split('.').pop();
          if (extension) {
            const mimeTypeMap = {
              'pdf': 'application/pdf',
              'txt': 'text/plain',
              'csv': 'text/csv',
              'doc': 'application/msword',
              'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'xls': 'application/vnd.ms-excel',
              'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'ppt': 'application/vnd.ms-powerpoint',
              'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
              'zip': 'application/zip'
            };

            if (mimeTypeMap[extension]) {
              console.log(`Mapped file extension .${extension} to MIME type:`, mimeTypeMap[extension]);
              return resolve(mimeTypeMap[extension]);
            }
          }
        }

        // If we can't determine the type, let FilePond handle it
        resolve(type);
      });
    }
  },
};
</script>
<style scoped>
.drop-box {
  border: 3px dashed rgba(25, 118, 210, 0.11) !important;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.file-pond-container {
  min-height: 200px;
  height: 100%;
}
</style>
